import { RunElement } from '../../shared/run-element.js';
import * as defaultSkin from './skins/avatar.default.js';
import * as wireframeSkin from './skins/avatar.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

// Public, frozen enumerations — consumed by tests and authoring tooling.
export const AVATAR_SIZES = Object.freeze(['xs', 'sm', 'md', 'lg', 'xl']);
export const AVATAR_SHAPES = Object.freeze(['circle', 'square']);
export const AVATAR_LOADING = Object.freeze(['lazy', 'eager']);
// Order is the resolution priority for `data-state` on the host.
export const AVATAR_IMAGE_STATES = Object.freeze(['loading', 'image', 'initials', 'icon']);

const DEFAULT_SIZE = 'md';
const DEFAULT_SHAPE = 'circle';
const DEFAULT_LOADING = 'lazy';

/**
 * run-avatar — a user/entity avatar.
 *
 * Resolution order for what is shown (reflected on the host as `data-state`):
 *   1. `image`    — `src` set and the image loaded successfully
 *   2. `loading`  — `src` set, image not yet loaded
 *   3. `initials` — no usable image, but `initials` or `name` yields initials
 *   4. `icon`     — nothing else; the skin paints a placeholder glyph
 *
 * The default skin reads `src`, `_imageState`, `_resolvedInitials`, `size`,
 * `shape` and `loading` off the host (the SkinModule.render contract), and the
 * stylesheet reveals the right part via `:host([data-state="…"])` — so the host
 * MUST keep `data-state` and those accessors in sync. The prior implementation
 * exposed none of them, which is why avatars rendered as empty circles
 * (JUNE-112: completed against avatar.test.js + the default skin contract).
 */
class RunAvatar extends RunElement {
  #src = null;
  #alt = null;
  #name = null;
  #initials = null;       // explicit initials override (null ⇒ derive from name)
  #size = DEFAULT_SIZE;
  #shape = DEFAULT_SHAPE;
  #decorative = false;
  #loading = DEFAULT_LOADING;

  #imageLoaded = false;   // internal <img> fired `load` for the current src
  #imageErrored = false;  // internal <img> fired `error` for the current src
  #imageState = 'icon';   // last computed value mirrored to data-state
  #rendering = false;

  static skinLoaders = {};

  static get observedAttributes() {
    return ['src', 'alt', 'name', 'initials', 'size', 'shape', 'decorative', 'loading'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'src':        this.src        = newValue; break;
      case 'alt':        this.alt        = newValue; break;
      case 'name':       this.name       = newValue; break;
      case 'initials':   this.initials   = newValue; break;
      case 'size':       this.size       = newValue === null ? DEFAULT_SIZE : newValue; break;
      case 'shape':      this.shape      = newValue === null ? DEFAULT_SHAPE : newValue; break;
      case 'decorative': this.decorative = newValue !== null; break;
      case 'loading':    this.loading    = newValue === null ? DEFAULT_LOADING : newValue; break;
    }
  }

  // ── src ─────────────────────────────────────────────────────────────────
  get src() { return this.#src; }
  set src(value) {
    let next;
    if (value == null) next = null;
    else { const s = String(value); next = s === '' ? null : s; }
    if (this.#src === next) return;
    this.#src = next;
    // A new (or cleared) src invalidates any prior load/error outcome.
    this.#imageLoaded = false;
    this.#imageErrored = false;
    if (next === null) this.removeAttribute('src');
    else this.setAttribute('src', next);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── alt ─────────────────────────────────────────────────────────────────
  get alt() { return this.#alt; }
  set alt(value) {
    const next = this.#nullableTrimmed(value);
    if (this.#alt === next) return;
    this.#alt = next;
    if (next === null) this.removeAttribute('alt');
    else this.setAttribute('alt', next);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── name ────────────────────────────────────────────────────────────────
  get name() { return this.#name; }
  set name(value) {
    const next = this.#nullableTrimmed(value);
    if (this.#name === next) return;
    this.#name = next;
    if (next === null) this.removeAttribute('name');
    else this.setAttribute('name', next);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── initials (explicit override) ────────────────────────────────────────
  get initials() { return this.#initials; }
  set initials(value) {
    let next;
    if (value == null) next = null;
    else {
      const s = String(value).trim();
      if (s === '') next = null;
      else next = this.#graphemes(s).slice(0, 2).join('').toLocaleUpperCase();
    }
    if (this.#initials === next) return;
    this.#initials = next;
    if (next === null) this.removeAttribute('initials');
    else this.setAttribute('initials', next);
    this.requestUpdate();
  }

  // ── size ────────────────────────────────────────────────────────────────
  get size() { return this.#size; }
  set size(value) {
    const coerced = String(value == null ? '' : value).trim();
    const resolved = AVATAR_SIZES.includes(coerced) ? coerced : DEFAULT_SIZE;
    if (this.#size === resolved) return;
    this.#size = resolved;
    this.setAttribute('size', resolved);
    this.requestUpdate();
  }

  // ── shape ───────────────────────────────────────────────────────────────
  get shape() { return this.#shape; }
  set shape(value) {
    const coerced = String(value == null ? '' : value).trim();
    const resolved = AVATAR_SHAPES.includes(coerced) ? coerced : DEFAULT_SHAPE;
    if (this.#shape === resolved) return;
    this.#shape = resolved;
    this.setAttribute('shape', resolved);
    this.requestUpdate();
  }

  // ── decorative ──────────────────────────────────────────────────────────
  get decorative() { return this.#decorative; }
  set decorative(value) {
    const next = Boolean(value);
    if (this.#decorative === next) return;
    this.#decorative = next;
    this.toggleAttribute('decorative', next);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── loading ─────────────────────────────────────────────────────────────
  get loading() { return this.#loading; }
  set loading(value) {
    const coerced = String(value == null ? '' : value).trim();
    const resolved = AVATAR_LOADING.includes(coerced) ? coerced : DEFAULT_LOADING;
    if (this.#loading === resolved) return;
    this.#loading = resolved;
    this.setAttribute('loading', resolved);
    this.requestUpdate();
  }

  // ── Skin-render contract accessors (read by avatar.default.js) ──────────
  get _resolvedInitials() {
    if (this.#initials != null) return this.#initials;
    return this.#deriveInitialsFromName();
  }

  get _imageState() { return this.#imageState; }

  // ── Render ──────────────────────────────────────────────────────────────
  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      this.#imageState = this.#computeImageState();
      this.setAttribute('data-state', this.#imageState);

      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }

      // Wire load/error on the freshly-rendered <img> (present only with src).
      // Property assignment (not addEventListener) is idempotent across the
      // re-renders the handlers themselves trigger — no duplicate listeners.
      const img = this.shadowRoot.querySelector('img');
      if (img) {
        img.onload = this.#onImgLoad;
        img.onerror = this.#onImgError;
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #onImgLoad = () => {
    if (this.#src == null || this.#imageLoaded) return;
    this.#imageLoaded = true;
    this.#imageErrored = false;
    this.dispatchEvent(new CustomEvent('run-load', {
      bubbles: true, composed: true, cancelable: false,
      detail: { src: this.#src },
    }));
    this.requestUpdate();
  };

  #onImgError = () => {
    if (this.#src == null || this.#imageErrored) return;
    this.#imageErrored = true;
    this.#imageLoaded = false;
    const fallback = this._resolvedInitials ? 'initials' : 'icon';
    this.dispatchEvent(new CustomEvent('run-error', {
      bubbles: true, composed: true, cancelable: false,
      detail: { src: this.#src, fallback },
    }));
    this.requestUpdate();
  };

  #computeImageState() {
    if (this.#src != null) {
      if (this.#imageErrored) return this._resolvedInitials ? 'initials' : 'icon';
      if (this.#imageLoaded) return 'image';
      return 'loading';
    }
    return this._resolvedInitials ? 'initials' : 'icon';
  }

  #resolveSkin() {
    const instanceSkin = this.getAttribute('skin');
    if (instanceSkin) return instanceSkin;
    let el = this.parentElement;
    while (el) {
      const s = el.getAttribute('data-run-skin');
      if (s) return s;
      el = el.parentElement;
    }
    return 'default';
  }

  #updateAria() {
    if (this.#decorative) {
      this.removeAttribute('role');
      this.setAttribute('aria-hidden', 'true');
      this.removeAttribute('aria-label');
      return;
    }
    this.setAttribute('role', 'img');
    this.removeAttribute('aria-hidden');
    const label = this.#alt != null ? this.#alt : this.#name;
    if (label != null) this.setAttribute('aria-label', label);
    else this.removeAttribute('aria-label');
  }

  #deriveInitialsFromName() {
    if (this.#name == null) return null;
    const words = this.#name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return null;
    const firstGrapheme = (w) => this.#graphemes(w)[0] || '';
    const raw = words.length === 1
      ? firstGrapheme(words[0])
      : firstGrapheme(words[0]) + firstGrapheme(words[words.length - 1]);
    const trimmed = this.#graphemes(raw).slice(0, 2).join('');
    return trimmed ? trimmed.toLocaleUpperCase() : null;
  }

  #graphemes(value) {
    const str = String(value);
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(str)]
        .map((seg) => seg.segment);
    }
    return Array.from(str);
  }

  #nullableTrimmed(value) {
    if (value == null) return null;
    const s = String(value).trim();
    return s === '' ? null : s;
  }
}

if (!customElements.get('run-avatar')) {
  customElements.define('run-avatar', RunAvatar);
}

export { RunAvatar };
export default RunAvatar;
